import React, { useState, useEffect } from 'react';
import './MappingSearch.css';
import { MappingFilters } from '../../types/columnMappingReview';

interface MappingSearchProps {
  filters: MappingFilters;
  onFilterChange: (filters: Partial<MappingFilters>) => void;
}

const MappingSearch: React.FC<MappingSearchProps> = ({ filters, onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState(filters.search || '');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Predefined domain options based on CLAUDE.md
  const domainOptions = [
    { value: '', label: 'All Domains' },
    { value: 'treasury', label: 'Treasury' },
    { value: 'ap', label: 'Accounts Payable' },
    { value: 'ar', label: 'Accounts Receivable' },
    { value: 'gl', label: 'General Ledger' },
    { value: 'revenue', label: 'Revenue' },
    { value: 'general', label: 'General' }
  ];

  // Debounce search input
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      if (searchTerm !== filters.search) {
        onFilterChange({ search: searchTerm || undefined });
      }
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm]);

  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onFilterChange({ domain: e.target.value || undefined });
  };

  const handleQuickFilter = (filterType: string) => {
    const now = new Date();
    switch (filterType) {
      case 'never-used':
        // This would need backend support for usage_count = 0 filter
        onFilterChange({ search: undefined, domain: undefined });
        break;
      case 'recently-modified':
        // This would need backend support for date filters
        onFilterChange({ sort_by: 'created_at', sort_order: 'desc' });
        break;
      case 'high-usage':
        onFilterChange({ sort_by: 'usage_count', sort_order: 'desc' });
        break;
      case 'stale':
        onFilterChange({ sort_by: 'last_used', sort_order: 'asc' });
        break;
      default:
        break;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    onFilterChange({
      search: undefined,
      domain: undefined,
      sort_by: 'last_used',
      sort_order: 'desc',
      page: 1
    });
  };

  return (
    <div className="mapping-search">
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search columns or metadata names..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select
          value={filters.domain || ''}
          onChange={handleDomainChange}
          className="filter-input"
        >
          {domainOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button 
          className="clear-button"
          onClick={clearFilters}
        >
          Clear Filters
        </button>
      </div>

      <div className="quick-filters">
        <span className="quick-filter-label">Quick Filters:</span>
        <button 
          className="quick-filter-btn"
          onClick={() => handleQuickFilter('never-used')}
        >
          Never Used
        </button>
        <button 
          className="quick-filter-btn"
          onClick={() => handleQuickFilter('stale')}
        >
          Stale (90+ days)
        </button>
        <button 
          className="quick-filter-btn"
          onClick={() => handleQuickFilter('recently-modified')}
        >
          Recently Modified
        </button>
        <button 
          className="quick-filter-btn"
          onClick={() => handleQuickFilter('high-usage')}
        >
          High Usage
        </button>
      </div>
    </div>
  );
};

export default MappingSearch;